import { Suspense, lazy } from 'react';
import { useQuery } from '@tanstack/react-query';
import { KeyRound, Link2, RotateCw } from 'lucide-react';
import { fetchPrototypeSchema } from '../api';
import { qk } from '../api/queryKeys';

// React Flow is heavy — keep it off the app's main bundle; it loads only when a
// prototype with a schema is actually viewed.
const PrototypeErdCanvas = lazy(() => import('./PrototypeErdCanvas'));

/**
 * Read-only visualization of a prototype's database, rendered from the live
 * `schema.prisma` the API parses out of the prototype repo. It always reflects
 * the deployed schema — when a prototype changes its models and redeploys, the
 * next fetch (invalidated on merge/redeploy) redraws the diagram. Renders
 * nothing for prototypes without a relational schema (e.g. static ones).
 */
export function PrototypeSchemaCard({ prototypeId }: { prototypeId: string }) {
  const schemaQuery = useQuery({
    queryKey: qk.prototypeSchema(prototypeId),
    queryFn: () => fetchPrototypeSchema(prototypeId),
  });

  if (schemaQuery.isLoading) {
    return (
      <div>
        <h2 className="mb-3 text-sm font-semibold text-ink">Database</h2>
        <div
          role="status"
          aria-live="polite"
          className="h-[380px] animate-pulse rounded-xl border border-line bg-surface-2 sm:h-[520px]"
        />
      </div>
    );
  }

  const schema = schemaQuery.data;
  // Hide entirely when there's no relational schema to show (static prototype,
  // schema not found, or a fetch error) — the DB diagram is additive, not a
  // gate on the rest of the page.
  if (!schema || !schema.available || schema.models.length === 0) return null;

  return (
    // Anchor target for the API card's schema-name links.
    <div id="prototype-erd" className="scroll-mt-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-ink">Database</h2>
        <div className="flex items-center gap-3">
          <span className="hidden items-center gap-3 text-xs text-ink-muted sm:flex">
            <span className="inline-flex items-center gap-1.5">
              <KeyRound className="h-3.5 w-3.5 text-warning" aria-hidden="true" /> primary key
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Link2 className="h-3.5 w-3.5 text-info" aria-hidden="true" /> foreign key
            </span>
          </span>
          <span className="text-xs text-ink-faint">
            {schema.models.length} {schema.models.length === 1 ? 'table' : 'tables'}
          </span>
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => schemaQuery.refetch()}
            disabled={schemaQuery.isFetching}
            title="Re-read the schema from the deployed commit"
          >
            <RotateCw
              className={`h-3 w-3 ${schemaQuery.isFetching ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            Refresh
          </button>
        </div>
      </div>
      <Suspense
        fallback={
          <div className="grid h-[380px] w-full place-items-center rounded-xl border border-line bg-surface-0 text-sm text-ink-faint sm:h-[520px]">
            Loading schema diagram…
          </div>
        }
      >
        <PrototypeErdCanvas models={schema.models} relations={schema.relations} />
      </Suspense>
      <p className="mt-2 text-xs text-ink-faint">
        Read-only view of the prototype's Prisma schema. Drag a table to
        rearrange, scroll to zoom, hover or tap a table to spotlight its
        relationships.
      </p>
    </div>
  );
}
